import { ComponentFactoryResolver, ComponentRef, Injectable, ViewContainerRef } from '@angular/core';
import { FormArray } from '@angular/forms';
import { IApplication } from '@app/core/model/applications/applications';
import { BsAppLaunchCheckboxControlComponent } from '../bs-app-launch-form-controls/bs-app-launch-checkbox-control/bs-app-launch-checkbox-control.component';
import { CheckBoxFormControl } from '../bs-app-launch-form-controls/bs-app-launch-checkbox-control/checkbox.control';
import { BsAppLaunchFieldSetControlComponent } from '../bs-app-launch-form-controls/bs-app-launch-field-set-control/bs-app-launch-field-set-control.component';
import { FieldSetFormControl } from '../bs-app-launch-form-controls/bs-app-launch-field-set-control/field-set.control';
import { BsAppLaunchResourceChooserControlComponent } from '../bs-app-launch-form-controls/bs-app-launch-resource-chooser-control/bs-app-launch-resource-chooser-control.component';
import { ResourceChooserFormControl } from '../bs-app-launch-form-controls/bs-app-launch-resource-chooser-control/resource-chooser.control';
import { SearchResourceType } from '@app/core/model/search/search-resource-type';
import _ from 'lodash';
import { IUser } from '@app/core/model/user';
import { BsAppLaunchTextAreaControlComponent } from '../bs-app-launch-form-controls/bs-app-launch-text-area-control/bs-app-launch-text-area-control.component';
import { TextAreaFormControl } from '../bs-app-launch-form-controls/bs-app-launch-text-area-control/text-area.control';
import { BsAppLaunchTextInputControlComponent } from '../bs-app-launch-form-controls/bs-app-launch-text-input-control/bs-app-launch-text-input-control.component';
import { TextBoxFormControl } from '../bs-app-launch-form-controls/bs-app-launch-text-input-control/text-box.control';
import { BasespaceFormArray, BasespaceFormControl, FormControlType } from '../bs-app-launch-form-controls/bs-app-launch-form-control';
import { SelectFormControl } from '../bs-app-launch-form-controls/bs-app-launch-select-control/select.control';
import { BsAppLaunchSelectControlComponent } from '../bs-app-launch-form-controls/bs-app-launch-select-control/bs-app-launch-select-control.component';
import { BsAppLaunchTabularFieldSetControlComponent } from '../bs-app-launch-form-controls/bs-app-launch-tabular-field-set-control/bs-app-launch-tabular-field-set-control.component';
import { TabularFieldSetFormControl } from '../bs-app-launch-form-controls/bs-app-launch-tabular-field-set-control/tabular-field-set.control';
import { BsAppLaunchNumericControlComponent } from '../bs-app-launch-form-controls/bs-app-launch-numeric-control/bs-app-launch-numeric-control.component';
import { NumericFormControl } from '../bs-app-launch-form-controls/bs-app-launch-numeric-control/numeric.control';
import { BsAppLaunchRadioButtonControlComponent } from '../bs-app-launch-form-controls/bs-app-launch-radio-button-control/bs-app-launch-radio-button-control.component';
import { RadioButtonFormControl } from '../bs-app-launch-form-controls/bs-app-launch-radio-button-control/radio-button.control';
import { BsAppLaunchSectionBreakControlComponent } from '../bs-app-launch-form-controls/bs-app-launch-section-break-control/bs-app-launch-section-break-control.component';
import { SectionBreakFormControl } from '../bs-app-launch-form-controls/bs-app-launch-section-break-control/section-break.control';
import { BsAppLaunchResourceMatcherControlComponent } from '../bs-app-launch-form-controls/bs-app-launch-resource-matcher-control/bs-app-launch-resource-matcher-control.component';
import { ResourceMatcherFormControl } from '../bs-app-launch-form-controls/bs-app-launch-resource-matcher-control/resource-matcher.control';
import { isNullOrUndefined } from 'util';
import { Subject } from 'rxjs';
import { StringUtilities } from '@app/core/utilities/string-utilities';

@Injectable({
  providedIn: 'root'
})
export class BsAppLaunchFormService {
  localDateTime: string;
  bioSampleRegistryEnabled: boolean;
  currentApp: IApplication;
  currentUser: IUser;
  formArray: FormArray;
  
  // Subject to emit when a control's value has changed and should trigger form updates
  public formUpdateTrigger$ = new Subject<{ controlId: string }>();
  

  constructor(
    private resolver: ComponentFactoryResolver,
  ) { }

  initializeForm(fields: any[], formContainer: ViewContainerRef, formArray: FormArray): void {
    fields.forEach((field) => {
      this.initializeFormField(field, formContainer, formArray);
    });
  }

  initializeFormField(rawField: any, formContainer: ViewContainerRef, formArray: FormArray, basespaceFormControl?: BasespaceFormControl<any> | BasespaceFormArray<any>): void {
    let componentType;
    let formControl = basespaceFormControl ? basespaceFormControl : null;
    const formControlParameterSupplied = !!formControl;
    const field = StringUtilities.deepCamelCase(rawField);

    // App session name is a special field as it's used as the 'Name' property for the app session and shows up in analyses master list.
    // This behavior of replacing the existing value with '<app-name> <date>' was carried over from old app launch UI.
    if(field.id === 'app-session-name') {
      field.value = `${this.currentApp.Name} [LocalDateTime]`;
    }
    
    switch (field.formControlType) {
      case FormControlType.TEXT_BOX:
          componentType = BsAppLaunchTextInputControlComponent;

          if(!formControl) {
            field.value = field.value ? field.value.replace("[LocalDateTime]", this.localDateTime) : field.value;
            formControl = new TextBoxFormControl({
              ...field,
            });
          }
          break;
      case FormControlType.FIELD_SET:
        componentType = BsAppLaunchFieldSetControlComponent;
        if(!formControl) {
          formControl = new FieldSetFormControl({
            ...field
          });
        }
        break;
      case FormControlType.TABULAR_FIELD_SET:
          componentType = BsAppLaunchTabularFieldSetControlComponent;
          if(!formControl) {
            formControl = new TabularFieldSetFormControl({
              ...field
            });
          }
          break;
        case FormControlType.RUN_CHOOSER:
        case FormControlType.PROJECT_CHOOSER:
        case FormControlType.V1_DATASET_FILE_CHOOSER:
        case FormControlType.FILE_CHOOSER:
        case FormControlType.V1_DATASET_CHOOSER:
        case FormControlType.APP_RESULT_CHOOSER:
        case FormControlType.SAMPLE_CHOOSER:
        case FormControlType.V1_BIOSAMPLE_CHOOSER:
          componentType = BsAppLaunchResourceChooserControlComponent;
          if(!formControl) {
            field.resourceType = this.getChooserResourceType(field.formControlType);
            formControl = new ResourceChooserFormControl({
              ...field
            });
          }
        break;
      case FormControlType.TEXT_AREA:
          componentType = BsAppLaunchTextAreaControlComponent;
          if(!formControl) {
            formControl = new TextAreaFormControl({
              ...field,
            });
           }
        break;
      case FormControlType.CHECK_BOX:
          componentType = BsAppLaunchCheckboxControlComponent;
          if(!formControl) {
            formControl = new CheckBoxFormControl({
              ...field
            });
          }
          break;
      case FormControlType.SELECT:
          componentType = BsAppLaunchSelectControlComponent;
          if(!formControl) { 
            formControl = new SelectFormControl({
              ...field
            });
          }
            break;
        case FormControlType.NUMERIC:
            componentType = BsAppLaunchNumericControlComponent;
            if(!formControl) {
              formControl = new NumericFormControl({
                ...field
              });
            }
            break;
        case FormControlType.RADIO_BUTTON:
            componentType = BsAppLaunchRadioButtonControlComponent;
            if(!formControl) {
              formControl = new RadioButtonFormControl({
                ...field
              });
            }
            break;
        case FormControlType.SECTION_BREAK:
            componentType = BsAppLaunchSectionBreakControlComponent;
            if(!formControl) { 
              formControl = new SectionBreakFormControl({
                ...field
              });
            }
            break;
        case "ResourceMatcher":
        case "V1V2ResourceMatcher":
            field.resourceType = this.getMatcherResourceType(field.formControlType, field.columns[0].resourceType);
            componentType = BsAppLaunchResourceMatcherControlComponent;
            if(!formControl) { 
              formControl = new ResourceMatcherFormControl({
                ...field
              });
            }
            break;
        default:
            console.log(`"${field.formControlType}" component type not implemented yet`);
            return;
      }

    const componentFactory = this.resolver.resolveComponentFactory(componentType);

    // Docs: GenerateFormFromAppSession within src/Cloud.Domain/Logic/FormPropertiesLogic.cs
    const componentRef = formContainer.createComponent(componentFactory) as ComponentRef<any>;
    formControl.componentReference = componentRef;
    if(!formControlParameterSupplied) {
      formArray.push(formControl);  
    }
    componentRef.instance.formControl = formControl;
  }

  getMatcherResourceType(componentName: string, columnResourceType: 'AppResult' | 'Sample'): SearchResourceType {
    switch(componentName) {
      case 'ResourceMatcher':
        return columnResourceType === 'AppResult' ? SearchResourceType.AppResult : SearchResourceType.Sample;
      case "V1V2ResourceMatcher":
        return columnResourceType === 'AppResult' ? SearchResourceType.Dataset : SearchResourceType.BiosampleLibraryPrep;
      default:
        return null;
    }
  }

  getChooserResourceType(componentName: string): SearchResourceType {
    switch(componentName) {
      case FormControlType.RUN_CHOOSER:
        return SearchResourceType.Run;
      case FormControlType.PROJECT_CHOOSER:
        return SearchResourceType.Project;
      case FormControlType.V1_DATASET_FILE_CHOOSER:
        return SearchResourceType.File;
      case FormControlType.FILE_CHOOSER:
        return SearchResourceType.AppResultFile;
      case FormControlType.V1_DATASET_CHOOSER:
        return SearchResourceType.Dataset;
      case FormControlType.APP_RESULT_CHOOSER:
        return SearchResourceType.AppResult;
      case FormControlType.SAMPLE_CHOOSER:
        return SearchResourceType.Sample;
      case FormControlType.V1_BIOSAMPLE_CHOOSER:
        return SearchResourceType.Biosample;
      default:
        return null;
    }

  }

  flattenLaunchForm(formArray: FormArray): BasespaceFormControl<any>[] {
      return formArray.controls.reduce((flattenedArray, current) => {
          return flattenedArray.concat((current instanceof BasespaceFormArray || current instanceof FormArray) ? this.flattenLaunchForm(current) : current);
      }, []);
  }

// Recursively searches for all controls of the specified type at any depth
getControlsByType<T extends BasespaceFormControl<any> | BasespaceFormArray<any>>(
  formArray: FormArray, 
  controlType: new (...args: any[]) => T
): T[] {
  const results: T[] = [];
  
  formArray.controls.forEach(control => {
    // Check if the current control is of the target type
    if (control instanceof controlType) {
      results.push(control as T);
    }
    
    // If the control is a FormArray or BasespaceFormArray, recursively search its children
    if (control instanceof BasespaceFormArray || control instanceof FormArray) {
      results.push(...this.getControlsByType(control, controlType));
    }
  });
  
  return results;
}

  getFormControlById(formControlId: string, formArray: FormArray): BasespaceFormControl<any> | BasespaceFormArray<any> {
      for (let i = 0; i < formArray.controls.length; i++) {
          const control = formArray.controls[i];
          const typedControl = control as BasespaceFormControl<any> | BasespaceFormArray<any>;
          
          // Check if the current control matches the ID
          if (typedControl.id === formControlId) {
              return typedControl;
          }
          
          // If the control is a FormArray or BasespaceFormArray, recursively search its children
          if (control instanceof BasespaceFormArray || control instanceof FormArray) {
              const found = this.getFormControlById(formControlId, control);
              if (found) {
                  return found;
              }
          }
      }
      return null;
  }

  // Documentation: https://bespin-developer.cloud-test.illumina.com/docs/content/documentation/apptools/formbuilder-overview#Togglers
  toggle(toggleOnValue: number | string, toggleOnField: string | number, togglerValues: (string | number)[]): void {
    let enableToggle

    // null value is special case to indicate that field should be enabled when there are no togglerValues
    // null value is used in the form definition but undefined is actully returned here so that's why we're just checking for falsy value rather than value === null
    if(isNullOrUndefined(toggleOnValue)) {
      enableToggle = togglerValues.length === 0;
    } else {
      const toggleValues = String(toggleOnValue).split(',');
      enableToggle = toggleValues.some((enableToggleValue) => {
        return togglerValues.some((togglerValue) => String(togglerValue) === enableToggleValue)
      });
    }

    const toggleFields = String(toggleOnField).split(',');
    toggleFields.forEach((fieldId) => {
      const formControl = this.getFormControlById(fieldId, this.formArray);
      if(formControl) {
          if(enableToggle) {
            formControl.enable();
          } else {
            formControl.disable();
          }
      } else {
        console.log(`Could not find form control with id '${fieldId}'`);
      }
    });

  }
}
